import { defineStore } from 'pinia'
import { Templates } from '@/mocks/templates'
import { Template, CanvasElement, ImageElement, GroupElement, RectElement } from '@/types/canvas'
import { Object as FabricObject, SerializedImageProps, Image, Group } from 'fabric'
import { WorkSpaceDrawType, propertiesToInclude } from '@/configs/canvas'
import { useMainStore } from './main'
import { ElementNames } from '@/types/elements'
import { ElLoading } from 'element-plus'
import useCanvasScale from '@/hooks/useCanvasScale'
import useCanvas from '@/views/Canvas/useCanvas'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'
import useCommon from '@/views/Canvas/useCommon'
import usePixi from '@/views/Canvas/usePixi'


interface UpdateElementData {
  id: string | string[]
  left?: number
  top?: number
  props: Partial<CanvasElement>
}

export interface TemplatesState {
  templateId: number
  templates: Template[]
  templateIndex: number
  fonts: any
  isPageLoading: boolean
}

export const useTemplatesStore = defineStore('Templates', {
  state: (): TemplatesState => ({
    // theme: theme, // 主题样式
    templateId: 0,
    templates: Templates, // 页面页面数据
    templateIndex: 0, // 当前页面索引
    fonts: [], // 当前页面索引
    isPageLoading: false, // Loading state for page transitions
    // fixedRatio: false, // 固定比例
    // slideUnit: 'mm', // 尺寸单位
    // slideName: '', // 模板名称
    // slideId: '', // 模板id
  }),

  getters: {
    currentTemplate(state) {
      return state.templates[state.templateIndex] as Template
    },
    currentTemplateWidth(state) {
      const currentTemplate = state.templates[state.templateIndex]
      return currentTemplate.width
    },

    currentTemplateHeight(state) {
      const currentTemplate = state.templates[state.templateIndex]
      return currentTemplate.height
    },

    currentTemplateElement(state) {
      const currentTemplate = state.templates[state.templateIndex]
      const [ canvas ] = useCanvas()
      const activeObject = (canvas as any).getActiveObject() as CanvasElement
      return currentTemplate.objects.filter((ele: any) => ele.id === activeObject.id)[0]
    }
  },

  actions: {
    async renderTemplate() {
      const [ canvas ] = useCanvas()
      if (!canvas) return
      
      const { initCommon } = useCommon()
      const { setCanvasSize, setCanvasTransform } = useCanvasScale()
      
      // Clear canvas first to likely show empty state/background
      ;(canvas as any).clear()
      ;(canvas as any).renderAll()
      
      // Removed 50ms delay for faster switching

      await (canvas as any).loadFromJSON(this.currentTemplate)

      this.setObjectFilter(this.currentTemplate.objects as any as CanvasElement[])
      setCanvasSize()
      initCommon()
      
      // Explicitly reset viewport to ensure content is visible and centered
      setCanvasTransform()
      
      // Set zoom to best fit on mobile after rendering
      if (typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|HarmonyOS/i.test(navigator.userAgent)) {
        setTimeout(() => {
          setCanvasSize()
          setCanvasTransform()
        }, 150)
      } else {
        // Desktop: extra safety check
        console.log('Canvas Render Debug:', {
          objects: (canvas as any).getObjects().length,
          width: (canvas as any).width,
          height: (canvas as any).height,
          zoom: (canvas as any).getZoom()
        });
        (canvas as any).requestRenderAll()
      }
      
      // Explicitly reset viewport to ensure content is visible and centered
      setCanvasTransform()
    },

    async renderElement() {
      const [ canvas ] = useCanvas()
      const { initCommon } = useCommon()
      const { setCanvasSize } = useCanvasScale()
      const mainStore = useMainStore()
      ;(canvas as any).discardActiveObject()
      mainStore.setCanvasObject(undefined)
      await (canvas as any).loadFromJSON(this.currentTemplate)
      setCanvasSize()
      initCommon()
    },

    modifedElement() {
      const [ canvas ] = useCanvas()
      const { addHistorySnapshot } = useHistorySnapshot()
      const canvasTemplate = (canvas as any).toObject(propertiesToInclude)
      this.templates[this.templateIndex].objects = canvasTemplate.objects as any
      this.templates[this.templateIndex].background = canvasTemplate.background
      this.templates[this.templateIndex].backgroundImage = canvasTemplate.backgroundImage
      addHistorySnapshot()
    },

    setClip(clip: number) {
      const { addHistorySnapshot } = useHistorySnapshot()
      this.templates.forEach(template => {
        template.clip = clip
      })
      addHistorySnapshot()
    },

    setSize(width: number, height: number, zoom: number) {
      const { initCommon } = useCommon()
      const { addHistorySnapshot } = useHistorySnapshot()
      this.templates.forEach(template => {
        template.width = width
        template.height = height
        template.zoom = zoom
        template.objects.filter(item => item.id === WorkSpaceDrawType).map(ele => {
          ele.width = width / zoom
          ele.height = height / zoom
        })
      })
      initCommon()
      addHistorySnapshot()
    },

    setObjectFilter(objects: CanvasElement[]) {
      objects.forEach(ele => {
        if ((ele as any).type.toLowerCase() === ElementNames.IMAGE) {
 //         this.setImageFilter(ele as ImageElement)
          // this.setImageMask(ele as ImageElement)
        }
        if ((ele as any).type.toLowerCase() === ElementNames.GROUP) {
          this.setObjectFilter(((ele as GroupElement).objects) as any as CanvasElement[])
        }
      })
    },

    setImageFilter(image: ImageElement) {
      // if (!image.pixiFilters) return
      // const [ pixi ] = usePixi()
      // pixi.postMessage({
      //   id: image.id,
      //   type: "filter",
      //   src: image.src,
      //   pixiFilters: JSON.stringify(image.pixiFilters),
      //   width: image.width,
      //   height: image.height
      // });
    },

    setImageMask(image: ImageElement) {
      // if (!image.mask) return
      // const [ pixi ] = usePixi()
      // pixi.postMessage({
      //   id: image.id,
      //   type: "mask",
      //   src: image.src,
      //   mask: JSON.stringify(image.mask),
      //   width: image.width,
      //   height: image.height
      // });
    },

    async changeTemplate(template: Template | Template[]) {
      const { setCanvasTransform } = useCanvasScale()
      const templates = Array.isArray(template) ? template : [template]
      this.templates = templates as any
      this.templateIndex = 0
      await this.renderTemplate()
      setCanvasTransform()
    },

    setTemplates(templates: Template[]) {
      this.templates = templates as any
    },

    setTemplateId(templateId: number) {
      this.templateId = templateId
      this.templates = []
    },

    setTemplateIndex(index: number) {
      this.templateIndex = index
    },

    clearTemplates() {
      this.templates = []
    },
    setFonts(fonts: any) {
      this.fonts = fonts
    },
    getFonts() {
      return this.fonts
    },
    async addTemplate(template: Template | Template[], index?: number) {
      const templates = Array.isArray(template) ? template : [template];
      const addIndex = index !== undefined ? index : (this.templateIndex >= 0 ? this.templateIndex + 1 : this.templates.length);
      this.templates.splice(addIndex, 0, ...templates as any);
      this.templateIndex = addIndex;
    },

    async getTemplates(rangeType = 'all') {
      if(rangeType === 'current'){
        return [this.templates[this.templateIndex]]
      }
     return this.templates
    },

    updateTemplate(props: Partial<Template>) {
      const { addHistorySnapshot } = useHistorySnapshot()
      const templateIndex = this.templateIndex
      this.templates[templateIndex] = { ...this.templates[templateIndex], ...props } as any
      addHistorySnapshot()
    },

    deleteTemplate(templateId: string | string[]) {
      const { addHistorySnapshot } = useHistorySnapshot()
      const templateIds = Array.isArray(templateId) ? templateId : [templateId]
  
      const deleteTemplatesIndex = []
      for (let i = 0; i < templateIds.length; i++) {
        const index = this.templates.findIndex(item => item.id === templateIds[i])
        deleteTemplatesIndex.push(index)
      }
      let newIndex = Math.min(...deleteTemplatesIndex)
  
      const maxIndex = this.templates.length - templateIds.length - 1
      if (newIndex > maxIndex) newIndex = maxIndex
  
      this.templateIndex = newIndex
      this.templates = this.templates.filter(item => !templateIds.includes(item.id))
      addHistorySnapshot()
    },

    updateWorkSpace(props: Partial<Template>) {
      const templateIndex = this.templateIndex
      this.templates[templateIndex] = { ...this.templates[templateIndex], ...props } as any
    },

    updateElement(data: UpdateElementData) {
      const { addHistorySnapshot } = useHistorySnapshot()
      const { id, props } = data
      const elementIds = typeof id === 'string' ? [id] : id
      if (!elementIds) return
      const template = this.templates[this.templateIndex]
      const elements = template.objects.map(el => elementIds.includes(el.id as string) ? { ...el, ...props }: el)
      this.templates[this.templateIndex].objects = elements as any
      addHistorySnapshot()
    },

    addElement(element: FabricObject | FabricObject[]) {
      const { addHistorySnapshot } = useHistorySnapshot()
      const elements = Array.isArray(element) ? element : [element]
      const currentTemplateElements = this.templates[this.templateIndex].objects
      const newElements = [...currentTemplateElements, ...elements]
      this.templates[this.templateIndex].objects = newElements as any
      addHistorySnapshot()
    },

    deleteElement(elementId: string | string[]) {
      const { addHistorySnapshot } = useHistorySnapshot()
      const elementIds = Array.isArray(elementId) ? elementId : [elementId]
      const currentTemplateElements = this.templates[this.templateIndex].objects
      const newElements = currentTemplateElements.filter(item => !elementIds.includes(item.id as string))
      this.templates[this.templateIndex].objects = newElements as any
      addHistorySnapshot()
    },

    setBackgroundImage(props: SerializedImageProps) {
      this.currentTemplate.backgroundImage = props
    },

  }
})